import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Users, IndianRupee, Activity, 
  Zap, Download, Calendar, RefreshCw, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Chart colors - dark green theme
const CHART_COLORS = {
  primary: '#0a5c44',
  secondary: '#10b981',
  tertiary: '#34d399',
  accent: '#6ee7b7',
  muted: '#a7f3d0'
};

const PIE_COLORS = ['#0a5c44', '#10b981', '#34d399', '#6ee7b7', '#a7f3d0', '#059669', '#047857'];

const AdvancedAnalytics = () => {
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Data states
  const [overview, setOverview] = useState(null);
  const [leadsTrend, setLeadsTrend] = useState([]);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [leadsBySource, setLeadsBySource] = useState([]);
  const [funnelConversion, setFunnelConversion] = useState([]);
  const [emailPerformance, setEmailPerformance] = useState([]);
  const [leadStatusDist, setLeadStatusDist] = useState([]);

  const fetchAllData = useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [
        overviewRes,
        leadsTrendRes,
        revenueTrendRes,
        sourceRes,
        funnelRes,
        emailRes,
        statusRes
      ] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/advanced-analytics/overview?period=${period}`, { headers }),
        axios.get(`${BACKEND_URL}/api/advanced-analytics/leads-trend?period=${period}`, { headers }),
        axios.get(`${BACKEND_URL}/api/advanced-analytics/revenue-trend?period=${period}`, { headers }),
        axios.get(`${BACKEND_URL}/api/advanced-analytics/leads-by-source?period=${period}`, { headers }),
        axios.get(`${BACKEND_URL}/api/advanced-analytics/funnel-conversion?period=${period}`, { headers }),
        axios.get(`${BACKEND_URL}/api/advanced-analytics/email-performance?period=${period}`, { headers }),
        axios.get(`${BACKEND_URL}/api/advanced-analytics/lead-status-distribution?period=${period}`, { headers })
      ]);

      setOverview(overviewRes.data);
      setLeadsTrend(leadsTrendRes.data.data || []);
      setRevenueTrend(revenueTrendRes.data.data || []);
      setLeadsBySource(sourceRes.data.data || []);
      setFunnelConversion(funnelRes.data.data || []);
      setEmailPerformance(emailRes.data.data || []);
      setLeadStatusDist(statusRes.data.data || []);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period]);

  useEffect(() => {
    setLoading(true);
    fetchAllData();
  }, [fetchAllData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAllData();
  };

  const handleExportCSV = async (dataType) => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(
        `${BACKEND_URL}/api/advanced-analytics/export-csv?period=${period}&data_type=${dataType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const blob = new Blob([res.data.csv_data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.data.filename;
      a.click();
      
      toast.success(`Exported ${res.data.rows} rows`);
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const ChangeIndicator = ({ value, suffix = '%' }) => {
    const isPositive = value >= 0;
    return (
      <span className={`flex items-center gap-1 text-sm font-medium ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
        {isPositive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
        {isPositive ? '+' : ''}{value.toFixed(1)}{suffix}
      </span>
    );
  };

  if (loading) {
    return (
      <div data-testid="analytics-loading" className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div data-testid="advanced-analytics-page" className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 md:mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Advanced Analytics</h1>
          <p className="text-sm md:text-base text-emerald-300/70">Comprehensive insights into your business performance</p>
        </div>
        <div className="flex items-center gap-2 md:gap-3 w-full md:w-auto flex-wrap">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger data-testid="period-selector" className="w-28 md:w-32 bg-[#0a3d2e] border-emerald-800/30 text-white">
              <Calendar className="w-4 h-4 mr-1 md:mr-2 text-emerald-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">7 Days</SelectItem>
              <SelectItem value="30d">30 Days</SelectItem>
              <SelectItem value="90d">90 Days</SelectItem>
              <SelectItem value="1y">1 Year</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="border-emerald-800/50 text-emerald-400 hover:bg-emerald-900/30"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={() => handleExportCSV('leads')}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0 }}>
          <Card data-testid="total-leads-card" className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <div className="p-2 md:p-3 rounded-xl bg-emerald-500/20">
                  <Users className="w-5 h-5 md:w-6 md:h-6 text-emerald-400" />
                </div>
                <ChangeIndicator value={overview?.lead_change || 0} />
              </div>
              <p className="text-emerald-300/70 text-xs md:text-sm">Total Leads</p>
              <p className="text-2xl md:text-3xl font-bold text-white">{overview?.total_leads || 0}</p>
              <p className="text-xs text-emerald-300/50 mt-1 hidden md:block">+{overview?.new_leads || 0} this period</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card data-testid="revenue-mrr-card" className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <div className="p-2 md:p-3 rounded-xl bg-blue-500/20">
                  <IndianRupee className="w-5 h-5 md:w-6 md:h-6 text-blue-400" />
                </div>
                <ChangeIndicator value={overview?.revenue_change || 0} />
              </div>
              <p className="text-emerald-300/70 text-xs md:text-sm">Revenue MRR</p>
              <p className="text-2xl md:text-3xl font-bold text-white">{formatCurrency(overview?.revenue_mrr || 0)}</p>
              <p className="text-xs text-emerald-300/50 mt-1 hidden md:block">vs last {period}</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card data-testid="conversion-rate-card" className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-4 md:p-6">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <div className="p-2 md:p-3 rounded-xl bg-purple-500/20">
                  <Activity className="w-5 h-5 md:w-6 md:h-6 text-purple-400" />
                </div>
                <ChangeIndicator value={overview?.conversion_change || 0} suffix=" pts" />
              </div>
              <p className="text-emerald-300/70 text-xs md:text-sm">Conversion Rate</p>
              <p className="text-2xl md:text-3xl font-bold text-white">{overview?.conversion_rate || 0}%</p>
              <p className="text-xs text-emerald-300/50 mt-1 hidden md:block">Form submissions / views</p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <Card data-testid="automations-card" className="bg-[#052e22]/50 border-emerald-800/30">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="p-3 rounded-xl bg-amber-500/20">
                  <Zap className="w-6 h-6 text-amber-400" />
                </div>
              </div>
              <p className="text-emerald-300/70 text-sm">Active Automations</p>
              <p className="text-3xl font-bold text-white">{overview?.active_automations || 0}</p>
              <p className="text-xs text-emerald-300/50 mt-1">Running workflows</p>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Leads Trend - Line Chart */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <Card data-testid="leads-trend-chart" className="bg-[#052e22]/50 border-emerald-800/30">
            <CardHeader className="border-b border-emerald-800/30 pb-4">
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
                Leads Trend
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 md:pt-6">
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={leadsTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e3a2f" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#6ee7b7" 
                    tick={{ fill: '#6ee7b7', fontSize: 10 }}
                    tickFormatter={(val) => val.slice(-5)}
                  />
                  <YAxis stroke="#6ee7b7" tick={{ fill: '#6ee7b7', fontSize: 10 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#052e22', 
                      border: '1px solid #0a5c44',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="leads" 
                    stroke={CHART_COLORS.primary} 
                    strokeWidth={2}
                    dot={{ fill: CHART_COLORS.secondary, strokeWidth: 1, r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>

        {/* Revenue Trend - Bar Chart */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
          <Card data-testid="revenue-trend-chart" className="bg-[#052e22]/50 border-emerald-800/30">
            <CardHeader className="border-b border-emerald-800/30 pb-4">
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-emerald-400" />
                Revenue by Month
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={revenueTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e3a2f" />
                  <XAxis 
                    dataKey="month" 
                    stroke="#6ee7b7" 
                    tick={{ fill: '#6ee7b7', fontSize: 12 }}
                  />
                  <YAxis stroke="#6ee7b7" tick={{ fill: '#6ee7b7', fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#052e22', 
                      border: '1px solid #0a5c44',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                    formatter={(value) => [formatCurrency(value), 'Revenue']}
                  />
                  <Bar 
                    dataKey="revenue" 
                    fill={CHART_COLORS.primary}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Leads by Source - Pie Chart */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
          <Card data-testid="leads-source-chart" className="bg-[#052e22]/50 border-emerald-800/30">
            <CardHeader className="border-b border-emerald-800/30 pb-4">
              <CardTitle className="text-lg font-semibold text-white">Leads by Source</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={leadsBySource}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="count"
                    nameKey="source"
                    label={({ source, percent }) => `${source} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {leadsBySource.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#052e22', 
                      border: '1px solid #0a5c44',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>

        {/* Lead Status Distribution - Donut Chart */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}>
          <Card data-testid="lead-status-chart" className="bg-[#052e22]/50 border-emerald-800/30">
            <CardHeader className="border-b border-emerald-800/30 pb-4">
              <CardTitle className="text-lg font-semibold text-white">Lead Status</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={leadStatusDist}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    dataKey="count"
                    nameKey="status"
                    label={({ status }) => status}
                    labelLine={false}
                  >
                    {leadStatusDist.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#052e22', 
                      border: '1px solid #0a5c44',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>

        {/* Funnel Conversion - Bar Chart */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}>
          <Card data-testid="funnel-conversion-chart" className="bg-[#052e22]/50 border-emerald-800/30">
            <CardHeader className="border-b border-emerald-800/30 pb-4">
              <CardTitle className="text-lg font-semibold text-white">Funnel Conversions</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {funnelConversion.length === 0 ? (
                <div className="h-[250px] flex items-center justify-center text-emerald-300/50">
                  No funnel data available
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={funnelConversion} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e3a2f" />
                    <XAxis type="number" stroke="#6ee7b7" tick={{ fill: '#6ee7b7', fontSize: 10 }} />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      stroke="#6ee7b7" 
                      tick={{ fill: '#6ee7b7', fontSize: 10 }}
                      width={80}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#052e22', 
                        border: '1px solid #0a5c44',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                      formatter={(value) => [`${value}%`, 'Conversion Rate']}
                    />
                    <Bar 
                      dataKey="conversion_rate" 
                      fill={CHART_COLORS.secondary}
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Charts Row 3 - Email Performance */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}>
        <Card data-testid="email-performance-chart" className="bg-[#052e22]/50 border-emerald-800/30">
          <CardHeader className="border-b border-emerald-800/30 pb-4">
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-emerald-400" />
              Email Performance
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {emailPerformance.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-emerald-300/50">
                No email performance data available
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={emailPerformance}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e3a2f" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#6ee7b7" 
                    tick={{ fill: '#6ee7b7', fontSize: 12 }}
                  />
                  <YAxis stroke="#6ee7b7" tick={{ fill: '#6ee7b7', fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#052e22', 
                      border: '1px solid #0a5c44',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="open_rate" 
                    name="Open Rate (%)"
                    stroke={CHART_COLORS.primary} 
                    strokeWidth={2}
                    dot={{ fill: CHART_COLORS.primary }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="click_rate" 
                    name="Click Rate (%)"
                    stroke={CHART_COLORS.tertiary} 
                    strokeWidth={2}
                    dot={{ fill: CHART_COLORS.tertiary }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default AdvancedAnalytics;
